import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { IAssetMetaData } from "../types/Asset.types";
import { getAssetWarning, isSameAsset } from "./assets.utils";

/**
 * Searches for the asset with the same metadata in the given list of assets.
 *
 * @param assets - Array of assets with metadata and balance.
 * @param asset - The asset metadata to search for.
 * @returns The asset with matching metadata if found, otherwise undefined.
 */
export const findAssetFromBalance = (
  assets: IMappedAsset<IAssetMetaData>[],
  asset: IAssetMetaData
) => {
  return assets.find((assetWithBalance) => isSameAsset(assetWithBalance.metadata, asset));
};

/**
 * Checks if the search input matches the asset's ticker or asset name.
 *
 * @param searchInput - The search input string.
 * @param asset - The asset metadata to check against.
 * @returns True if the search input matches the asset's ticker or asset name, otherwise false.
 */
export const matchesSearchInput = (searchInput: string, asset: IAssetMetaData) => {
  return (
    asset.ticker?.toLowerCase().includes(searchInput) ||
    asset.assetName?.toLowerCase().includes(searchInput)
  );
};

/**
 * Checks if the given asset is already added in the accumulator.
 *
 * @param acc - The accumulator array of assets.
 * @param asset - The asset metadata to check.
 * @returns True if the asset is already added in the accumulator, otherwise false.
 */
export const assetAlreadyAdded = (acc: IMappedAsset<IAssetMetaData>[], asset: IAssetMetaData) => {
  return acc.some((existingAsset) => isSameAsset(existingAsset.metadata, asset));
};

/**
 * Processes an asset to check if it matches the search input and if it's not
 * already added in the accumulator. If both conditions are met, it pushes
 * the asset with balance if available, otherwise with zero balance.
 *
 * @param acc - The accumulator array of assets.
 * @param searchInput - The search input string.
 * @param asset - The asset metadata to process.
 * @param assets - Array of assets with metadata and balance.
 */
export const processAsset = (
  acc: IMappedAsset<IAssetMetaData>[],
  searchInput: string,
  asset: IAssetMetaData,
  assets: IMappedAsset<IAssetMetaData>[]
) => {
  if (matchesSearchInput(searchInput, asset) && !assetAlreadyAdded(acc, asset)) {
    const availableAsset = findAssetFromBalance(assets, asset);
    acc.push(
      availableAsset
        ? availableAsset
        : {
            metadata: { ...asset, warning: getAssetWarning(asset) },
            quantity: new AssetAmount(0n),
          }
    );
  }
};
